import React, { useEffect, useState } from 'react';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableBrandFilterApplied,
  fetchAllBrands,
  // fetchBrandFilters,
  refreshBrands,
  setBrandLimit,
  setBrandPage,
  setBrandSortBy,
  setBrandSortField,
  // updateBrandFilters,
} from '../../../redux/slices/BrandSlice';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import deviceApi from '../../../utils/api/device-hub';
import AddBrandDrawer from '../../ev-device-hub/components/drawers/AddBrandDrawer';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Local brand management component — displays brands belonging to the current tenant.
 * @returns {React.JSX.Element} Local brand management component
 */
function Local(): React.JSX.Element {
  const dispatch = useAppDispatch();
  const {
    brands,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    filters,
    isFilterUpdated,
    // filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.brand);

  const [brandDrawerOpen, setBrandDrawerOpen] = useState(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: null,
    header: false,
    content: null,
    notificationModal: false,
  });

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllBrands({
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllBrands({
          page: DefaultApiLimit.page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableBrandFilterApplied());
    }
  }, [isFilterUpdated]);

  /**
   * Handles sort change from CustomTable
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setBrandSortBy(sortData.sortBy));
    dispatch(setBrandSortField(sortData.sortField));
    dispatch(
      fetchAllBrands({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and limit changes
   * @param {any} value - Page and limit data
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setBrandPage(value.page));
    if (value.limit !== undefined) dispatch(setBrandLimit(value.limit));
    dispatch(
      fetchAllBrands({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes brand data using current filters
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllBrands({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes brand data using current filters for table refresh
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      refreshBrands({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Calls the API to delete a brand
   * @param {string} brandId - The ID of the brand to delete
   */
  const handleConfirmDeleteBrand = (brandId: string) => {
    deviceApi
      .deleteBrand(brandId)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title="Brand deleted successfully"
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                handleRefresh();
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong',
            },
          }),
        );
      });
  };

  /**
   * Triggers the delete confirmation warning modal for a brand
   * @param {string} brandId - The ID of the brand
   */
  const handleDeleteBrandClick = (brandId: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={brandId}
          title="Are you sure you want to delete this brand?"
          description="This action cannot be undone."
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(id) => id && handleConfirmDeleteBrand(id)}
        />
      ),
      notificationModal: true,
    });
  };

  const column = [
    {
      label: 'Brand Name',
      accessorKey: 'name',
    },
    {
      label: 'Company Name',
      accessorKey: 'companyName',
    },
    {
      label: 'Country',
      accessorKey: 'country',
    },
    {
      label: 'Contact Email',
      accessorKey: 'email',
      /**
       * Renders contact email
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.email || 'N/A'}</span>,
    },
    {
      label: 'Phone Number',
      accessorKey: 'phoneNumber',
      /**
       * Renders phone number
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>
          {currentRow.phoneNumber
            ? `${currentRow.countryCode || ''} ${currentRow.phoneNumber}`
            : 'N/A'}
        </span>
      ),
    },
    {
      label: 'Device Count',
      accessorKey: 'deviceCount',
      sortAllow: true,
      /**
       * Renders device count
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.deviceCount || 0}</span>,
    },
    {
      label: 'Actions',
      accessorKey: 'actions',
      sortAllow: false,
      columnWidth: 50,
      /**
       * Renders row action buttons
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <div className="action-buttons-wrap">
          {permissionCheck('devicesBrandsAdd') && (
            <div
              role="presentation"
              className="action-button"
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteBrandClick(currentRow._id);
              }}>
              <DeleteIcon className="icon delete" />
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />

      <CustomTable
        dataKey="BrandManagement"
        header={column}
        data={brands}
        serialNo
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        height={350}
        created
        lastUpdated
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={handlePageLimitChange}
        sizeChangeButtonRequired
        // filters={filters}
        // filtersToDispatch={updateBrandFilters}
        // filterListLoading={filterListLoading}
        // filterListToDispatch={fetchBrandFilters}
        actionButton
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel="Refresh"
        refershCurrentPages={handleRefresh}
        onHiddenColumnsChange={() => {}}
        rightTopContent={
          <div className="table-bottom-button-list">
            {permissionCheck('devicesBrandsAdd') && (
              <div className="table-button-item">
                <MyButton
                  label="Add Brand"
                  buttonType="submit"
                  onClickFunc={() => setBrandDrawerOpen(true)}
                />
              </div>
            )}
          </div>
        }
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
      />

      <AddBrandDrawer
        open={brandDrawerOpen}
        onClose={() => setBrandDrawerOpen(false)}
        onBrandCreated={() => {
          setBrandDrawerOpen(false);
          handleRefresh();
        }}
      />
    </>
  );
}

export default Local;
